import { buildDateAtTime, buildDateFromLocalParts, getLocalDateParts, getWeekDayForLocalDate, incrementLocalDate, toTimeParts, } from './date-timezone-utils';
const DEFAULT_END_OF_DAY_TIME = '17:00';
const MAX_BUSINESS_DAY_LOOKAHEAD = 14;
const DAYS_IN_WEEK = 7;
const MONDAY = 1;
const NINE_AM = '09:00';
function toMinutes(hour, minute) {
    return hour * 60 + minute;
}
function getTimeToMinutes(time) {
    const parts = toTimeParts(time);
    return parts ? toMinutes(parts.hour, parts.minute) : null;
}
export function isInHoliday(now, holidays) {
    if (!holidays?.length) {
        return false;
    }
    const nowMs = now.getTime();
    return holidays.some((holiday) => {
        const start = new Date(holiday.holidayStart).getTime();
        const end = new Date(holiday.holidayEnd).getTime();
        if (!Number.isFinite(start) || !Number.isFinite(end)) {
            return false;
        }
        return nowMs >= start && nowMs < end;
    });
}
export function isInTimeWindow(dateWindow, context) {
    const { weekDay, hour, minute } = getLocalDateParts(context.currentTime, context.timezone);
    if (dateWindow.weekDay !== weekDay) {
        return false;
    }
    const fromMinutes = getTimeToMinutes(dateWindow.timeFrom);
    const toWindowMinutes = getTimeToMinutes(dateWindow.timeTo);
    if (fromMinutes === null || toWindowMinutes === null) {
        return false;
    }
    const currentMinutes = toMinutes(hour, minute);
    return currentMinutes >= fromMinutes && currentMinutes < toWindowMinutes;
}
export function isWithinBusinessHours(context) {
    if (isInHoliday(context.currentTime, context.holidays))
        return false;
    if (!context.businessHours?.length)
        return false;
    return context.businessHours.some((window) => isInTimeWindow(window, context));
}
export function hasRemainingBusinessHoursToday(context) {
    if (!context.businessHours?.length || isInHoliday(context.currentTime, context.holidays)) {
        return false;
    }
    const { hour, minute, weekDay } = getLocalDateParts(context.currentTime, context.timezone);
    const currentMinutes = toMinutes(hour, minute);
    return context.businessHours.some((window) => {
        if (window.weekDay !== weekDay) {
            return false;
        }
        const endMinutes = getTimeToMinutes(window.timeTo);
        return endMinutes !== null && endMinutes > currentMinutes;
    });
}
function* iterateBusinessDays(from, context, options) {
    const hours = context.businessHours ?? [];
    const localFrom = getLocalDateParts(from, context.timezone);
    let candidate = {
        year: localFrom.year,
        month: localFrom.month,
        day: localFrom.day,
    };
    if (!options?.includeToday) {
        candidate = incrementLocalDate(candidate);
    }
    for (let i = 0; i < MAX_BUSINESS_DAY_LOOKAHEAD; i += 1) {
        const weekDay = getWeekDayForLocalDate(candidate, context.timezone);
        const dayWindows = hours.filter((window) => window.weekDay === weekDay);
        if (dayWindows.length) {
            const dayStart = buildDateFromLocalParts({
                ...candidate,
                hour: 0,
                minute: 0,
            }, context.timezone);
            if (!isInHoliday(dayStart, context.holidays)) {
                yield { candidate, dayWindows };
            }
        }
        candidate = incrementLocalDate(candidate);
    }
}
export function nextWeekdayAtTime(from, context, preferredTime) {
    const { weekDay } = context;
    if (typeof weekDay !== 'number' ||
        !Number.isInteger(weekDay) ||
        weekDay < 0 ||
        weekDay > 6) {
        return null;
    }
    const preferredTimeParts = toTimeParts(preferredTime);
    if (!preferredTimeParts) {
        return null;
    }
    const localFrom = getLocalDateParts(from, context.timezone);
    let daysUntil = (weekDay - localFrom.weekDay + DAYS_IN_WEEK) % DAYS_IN_WEEK;
    let candidate = {
        year: localFrom.year,
        month: localFrom.month,
        day: localFrom.day,
    };
    if (daysUntil === 0) {
        if (context.includeToday === false) {
            daysUntil = DAYS_IN_WEEK;
        }
        else {
            const todayAtTime = buildDateFromLocalParts({
                ...candidate,
                hour: preferredTimeParts.hour,
                minute: preferredTimeParts.minute,
            }, context.timezone);
            if (todayAtTime < from) {
                daysUntil = DAYS_IN_WEEK;
            }
        }
    }
    for (let i = 0; i < daysUntil; i += 1) {
        candidate = incrementLocalDate(candidate);
    }
    for (let i = 0; i < MAX_BUSINESS_DAY_LOOKAHEAD; i += 1) {
        const deadline = buildDateFromLocalParts({
            ...candidate,
            hour: preferredTimeParts.hour,
            minute: preferredTimeParts.minute,
        }, context.timezone);
        if (!isInHoliday(deadline, context.holidays)) {
            return deadline;
        }
        candidate = incrementLocalDate(candidate);
    }
    return null;
}
export function nextMondayAtNine(from, timezone) {
    return nextWeekdayAtTime(from, {
        timezone,
        weekDay: MONDAY,
        includeToday: false,
    }, NINE_AM);
}
export function nextBusinessDay(from, context, preferredTime) {
    const preferredTimeParts = toTimeParts(preferredTime);
    if (!preferredTimeParts)
        return null;
    for (const { candidate } of iterateBusinessDays(from, context, { includeToday: false })) {
        return buildDateFromLocalParts({
            ...candidate,
            hour: preferredTimeParts.hour,
            minute: preferredTimeParts.minute,
        }, context.timezone);
    }
    return null;
}
export function findBusinessDayEnd(from, context, fallbackTime = DEFAULT_END_OF_DAY_TIME) {
    const hours = context.businessHours ?? [];
    for (const { candidate } of iterateBusinessDays(from, context, {
        includeToday: true,
    })) {
        // Find the latest end time for this day
        const weekDay = getWeekDayForLocalDate(candidate, context.timezone);
        const dayEndWindows = hours.filter((window) => window.weekDay === weekDay);
        const latestEndMinutes = dayEndWindows.reduce((latest, window) => {
            const endMinutes = getTimeToMinutes(window.timeTo);
            if (endMinutes === null) {
                return latest;
            }
            if (latest === null || endMinutes > latest) {
                return endMinutes;
            }
            return latest;
        }, null);
        if (latestEndMinutes === null)
            continue;
        const deadline = buildDateFromLocalParts({
            ...candidate,
            hour: Math.floor(latestEndMinutes / 60),
            minute: latestEndMinutes % 60,
        }, context.timezone);
        // "at end of day" remains valid when the current time is exactly day end
        if (deadline >= from) {
            return deadline;
        }
    }
    return buildDateAtTime(from, fallbackTime, context.timezone);
}
